import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { env } from '../../../../environments/environment';
import { Observable, map } from 'rxjs';
import { BBox, GeoJsonFeatureCollection } from '../../../shared/models/geojson';
import {
  ApiCollectionElement,
  ApiListResponse,
  ApiSingleResponse,
} from '../../../shared/models/api-collection.models';

export interface CollectionData {
  id: string;
  enteId?: number;
  titolo: string;
  ente: string;
  tipologia: string;
  indirizzo?: string;
  proprietario?: string;
  risorseContenute?: number;
  tipologiaBene?: string;
  tipologiaBeniCodici: string[];
  tipologiaRisorse?: string;
  tipologieRisorseCodici: string[];
  metadati?: boolean;
  accessoOnline: boolean;
  descrizione?: string;
  note?: string;
  url?: string;
  accessoInLoco?: boolean;
  modalitaAccesso?: string;
  licenza?: string;
  image?: string;
}

/** Estende CollectionData con le coordinate GeoJSON [lng, lat] per Leaflet */
export interface CollectionPoint extends CollectionData {
  coordinates: [number, number];
}

/**
 * Raggruppa le collezioni per ente, con centroide calcolato
 * dalle coordinate individuali — usato per i marker ente-cluster sulla mappa.
 */
export interface CollectionGroup {
  ente: string;
  indirizzo?: string;
  centroid: [number, number]; // [lng, lat]
  collections: CollectionData[];
}

@Injectable({ providedIn: 'root' })
export class MapClusteringService {
  private http = inject(HttpClient);

  private urlCollections = `${env.api.baseUrlPublic}/collections`;
  private urlGeo = `${env.api.baseUrlPublic}/geo`;

  /**
   * GET /public/collections per ente (drawer): include anche collezioni senza coordinate mappa.
   */
  searchCollectionsByEnte(
    enteId: number,
    filters?: {
      tipologieBeniIn?: string[];
      tipologieRisorseIn?: string[];
      accessoOnline?: boolean;
    },
  ): Observable<CollectionData[]> {
    return this.http
      .get<ApiListResponse<ApiCollectionElement>>(this.urlCollections, {
        params: this.buildCollectionsParams({
          enteId,
          ...filters,
          page: 0,
          totalElementsForPage: 10000,
        }),
      })
      .pipe(
        map(res =>
          (res.elements ?? []).map((el, index) => this.mapToCollectionData(el, index)),
        ),
      );
  }

  /**
   * GET /public/collections
   * Restituisce le collezioni mappate a CollectionPoint[].
   * Solo le collezioni con coordinate valide vengono incluse.
   */
  searchCollections(filters?: {
    id?: number;
    enteId?: number;
    tipologieBeniIn?: string[];
    tipologieRisorseIn?: string[];
    accessoOnline?: boolean;
    searchLike?: string;
    page?: number;
    totalElementsForPage?: number;
  }): Observable<CollectionPoint[]> {
    return this.http
      .get<ApiListResponse<ApiCollectionElement>>(this.urlCollections, {
        params: this.buildCollectionsParams(filters),
      })
      .pipe(
        map(res =>
          (res.elements ?? [])
            .map((el, index) => this.mapToCollectionPoint(el, index))
            .filter((p): p is CollectionPoint => p !== null),
        ),
      );
  }

  /** Page size per typeahead/dropdown ricerca homepage (tutti i match applicabili lato BE). */
  private static readonly searchSuggestionsPageSize = 20000;

  /**
   * Suggerimenti typeahead per la barra di ricerca (GET /collections con searchLike).
   */
  suggestCollections(
    searchLike: string,
    filters?: {
      tipologieBeniIn?: string[];
      tipologieRisorseIn?: string[];
      accessoOnline?: boolean;
    },
    limit = MapClusteringService.searchSuggestionsPageSize,
  ): Observable<CollectionPoint[]> {
    return this.searchCollections({
      ...filters,
      searchLike,
      page: 0,
      totalElementsForPage: limit,
    });
  }

  /**
   * GET /public/geo
   * Restituisce i dati GeoJSON clusterizzati per la viewport/zoom corrente.
   */
  getGeoData(
    bounds: { minLon: number; minLat: number; maxLon: number; maxLat: number },
    zoom: number = 6,
    filters?: {
      tipologieBeniIn?: string[];
      tipologieRisorseIn?: string[];
      accessoOnline?: boolean;
      searchLike?: string;
    },
  ): Observable<GeoJsonFeatureCollection> {
    let params = new HttpParams()
      .set('minLon', String(bounds.minLon))
      .set('minLat', String(bounds.minLat))
      .set('maxLon', String(bounds.maxLon))
      .set('maxLat', String(bounds.maxLat))
      .set('zoom', String(zoom));

    if (filters?.tipologieBeniIn?.length) {
      for (const v of filters.tipologieBeniIn) {
        params = params.append('tipologieBeniIn', v);
      }
    }

    if (filters?.tipologieRisorseIn?.length) {
      for (const v of filters.tipologieRisorseIn) {
        params = params.append('tipologieRisorseIn', v);
      }
    }

    if (filters?.accessoOnline) {
      params = params.set('accessoOnline', 'true');
    }

    if (filters?.searchLike?.trim()) {
      params = params.set('searchLike', filters.searchLike.trim());
    }

    return this.http
      .get<ApiSingleResponse<GeoJsonFeatureCollection>>(this.urlGeo, {
        params,
      })
      .pipe(
        map(
          res =>
            res.element ?? {
              type: 'FeatureCollection' as const,
              bbox: [0, 0, 0, 0] as BBox,
              features: [],
            },
        ),
      );
  }

  /** Raggruppa i punti per ente con centroide calcolato */
  buildGroupedCollections(points: CollectionPoint[]): CollectionGroup[] {
    const groups = new Map<string, CollectionPoint[]>();
    for (const c of points) {
      if (!groups.has(c.ente)) groups.set(c.ente, []);
      groups.get(c.ente)!.push(c);
    }
    return Array.from(groups.entries()).map(([ente, pts]) => {
      const centroid: [number, number] = [
        pts.reduce((s, p) => s + p.coordinates[0], 0) / pts.length,
        pts.reduce((s, p) => s + p.coordinates[1], 0) / pts.length,
      ];
      return {
        ente,
        indirizzo: pts[0].indirizzo,
        centroid,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        collections: pts.map(({ coordinates: _coords, ...rest }) => rest),
      };
    });
  }

  /** Rimuove le coordinate dai CollectionPoint → CollectionData[] */
  toCollectionData(points: CollectionPoint[]): CollectionData[] {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    return points.map(({ coordinates: _coords, ...rest }) => rest);
  }

  /** Mappa un elemento API a CollectionPoint. Ritorna null se mancano le coordinate. */
  private mapToCollectionPoint(el: ApiCollectionElement, index = 0): CollectionPoint | null {
    const data = this.mapToCollectionData(el, index);
    const ownerAddr = el.datiAnagrafici?.proprietarioCollezione?.indirizzo;
    const mappaAddr = el.visualizzazioneMappa?.indirizzo;
    const geoAddr = this.hasValidCoordinates(mappaAddr) ? mappaAddr : ownerAddr;
    if (!this.hasValidCoordinates(geoAddr)) return null;

    return {
      ...data,
      coordinates: [geoAddr!.longitudine, geoAddr!.latitudine],
    };
  }

  /** Mappa un elemento API a CollectionData (senza richiedere coordinate). */
  private mapToCollectionData(el: ApiCollectionElement, index = 0): CollectionData {
    const ownerAddr = el.datiAnagrafici?.proprietarioCollezione?.indirizzo;
    const mappaAddr = el.visualizzazioneMappa?.indirizzo;
    const geoAddr = this.hasValidCoordinates(mappaAddr) ? mappaAddr : ownerAddr;

    const mappaLabel = mappaAddr?.indirizzoEsteso
      ?? [mappaAddr?.indirizzo, mappaAddr?.civico, mappaAddr?.cap, mappaAddr?.comune, mappaAddr?.provincia]
          .filter(Boolean).join(', ');
    const indirizzoLabel = mappaLabel || geoAddr?.indirizzoEsteso || ownerAddr?.indirizzoEsteso || '';

    return {
      id: this.collectionElementId(el, index),
      enteId: el.datiAnagrafici?.proprietarioCollezione?.id,
      titolo: (el.titolo ?? '').replace(/\s+/g, ' ').trim(),
      ente: el.datiAnagrafici?.proprietarioCollezione?.nominativo ?? '',
      tipologia: el.fonte ?? el.tipologieBeni?.[0]?.descrizione ?? '',
      indirizzo: indirizzoLabel,
      proprietario: el.datiAnagrafici?.proprietarioCollezione?.nominativo ?? '',
      tipologiaBene: this.sortTipologie(el.tipologieBeni).map(t => t.descrizione).join(', '),
      tipologiaBeniCodici: this.sortTipologie(el.tipologieBeni).map(t => t.codice),
      tipologiaRisorse: this.sortTipologie(el.tipologieRisorse).map(t => t.descrizione).join(', '),
      tipologieRisorseCodici: this.sortTipologie(el.tipologieRisorse).map(t => t.codice),
      metadati: !!el.metadati,
      accessoOnline: el.accesso?.accessoOnline ?? false,
      descrizione: el.descrizione ?? '',
      note: el.note ?? '',
      url: el.accesso?.url ?? '',
      accessoInLoco: el.accesso?.accessoInLoco ?? false,
      modalitaAccesso: el.accesso?.modalitaAccesso ?? '',
      licenza: el.accesso?.licenze?.[0]?.descrizione ?? '',
      image: (el.visualizzazioneMappa?.immagini?.find((img: any) => img?.isCopertina) ?? el.visualizzazioneMappa?.immagini?.[0])?.logoImmagine ?? this.fallbackImage(el.fonte),
    };
  }

  /** Ordine stabile: il BE espone tipologie come Set senza ordine garantito. */
  private sortTipologie<T extends { codice?: string; descrizione?: string }>(items?: T[]): T[] {
    return [...(items ?? [])].sort((a, b) =>
      (a.codice ?? a.descrizione ?? '').localeCompare(b.codice ?? b.descrizione ?? '', 'it')
    );
  }

  /**
   * Id stabile per le collezioni pubbliche (il DTO non espone sempre `id`).
   * Stesso algoritmo su typeahead e su GET con idProprietario.
   */
  private collectionElementId(el: ApiCollectionElement, index: number): string {
    if (el.id != null) return String(el.id);
    if (el.shareGlobalId) return el.shareGlobalId;
    if (el.shareParentGlobalId) return el.shareParentGlobalId;
    const enteId = el.datiAnagrafici?.proprietarioCollezione?.id;
    const titolo = (el.titolo ?? '').replace(/\s+/g, ' ').trim();
    if (enteId != null && titolo) return `${enteId}::${titolo}`;
    return `gen-${index}`;
  }

  private buildCollectionsParams(filters?: {
    id?: number;
    enteId?: number;
    tipologieBeniIn?: string[];
    tipologieRisorseIn?: string[];
    accessoOnline?: boolean;
    searchLike?: string;
    page?: number;
    totalElementsForPage?: number;
  }): HttpParams {
    let params = new HttpParams()
      .set('paginazione.totalElementsForPage', String(filters?.totalElementsForPage ?? 10000))
      .set('paginazione.page', String(filters?.page ?? 0));

    if (filters?.id != null) {
      params = params.set('idEqual', String(filters.id));
    }

    if (filters?.enteId != null) {
      params = params.set('idProprietario', String(filters.enteId));
    }

    if (filters?.tipologieBeniIn?.length) {
      for (const v of filters.tipologieBeniIn) {
        params = params.append('tipologieBeniIn', v);
      }
    }

    if (filters?.tipologieRisorseIn?.length) {
      for (const v of filters.tipologieRisorseIn) {
        params = params.append('tipologieRisorseIn', v);
      }
    }

    if (filters?.accessoOnline) {
      params = params.set('accessoOnline', 'true');
    }

    if (filters?.searchLike?.trim()) {
      params = params.set('searchLike', filters.searchLike.trim());
    }

    return params;
  }

  private hasValidCoordinates(addr?: { latitudine?: number; longitudine?: number } | null): boolean {
    if (!addr) return false;
    return !(addr.latitudine === 0 && addr.longitudine === 0)
      && addr.latitudine != null
      && addr.longitudine != null;
  }

  private readonly fallbackImages: Record<string, string> = {
    ipac: 'assets/img/Default-IPaC.png',
    mp: 'assets/img/Default-Mappa.png',
    share: 'assets/img/Default-SHARE.png',
  };

  /** Seleziona l'immagine di default in base alla fonte */
  private fallbackImage(fonte: string | null | undefined): string {
    const key = (fonte ?? '').toLowerCase();
    return this.fallbackImages[key] ?? 'assets/img/Default-Mappa.png';
  }
}
